<template>
  <div class="pa-form-group" role="switch" :aria-checked="shouldBeChecked">
    <input
      :id="value"
      type="checkbox"
      :value="value"
      :disabled="loading || disabled"
      :checked="shouldBeChecked"
      :aria-checked="shouldBeChecked"
      :class="computedClass"
      @change="updateInput"
      tabindex="0"
    />
    <label :for="value">{{ label }}</label>
  </div>
</template>

<script>
import themeSetupMixin from '@/mixins/theme';

export default {
  mixins: [themeSetupMixin],
  name: 'SwitchInput',
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    value: {
      type: String,
    },
    /**
     * @model
     * */
    modelValue: {
      default: false,
    },
    label: {
      required: true,
      type: String,
    },
    checked: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    /**
     * indicated the loading state of
     * the switch component
     * @default is false
     * */
    loading: {
      default: false,
      type: Boolean,
    },
    trueValue: {
      default: true,
    },
    falseValue: {
      default: false,
    },
    /**
     * The type of the switch
     * @values primary, secondary, success, danger
     * @default is primary
     * */
    type: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'secondary', 'success', 'danger'].indexOf(value) !== -1,
    },
    /**
     * The size of the switch
     * @values small, medium, large
     * @default is medium
     * */
    size: {
      type: String,
      default: 'medium',
      validator: value => ['small', 'medium', 'large'].indexOf(value) !== -1,
    },
  },
  computed: {
    /**
     * This checks if the v-model whether an array or string includes/contains/is the value,
     * to update the check status
     * */
    shouldBeChecked() {
      if (this.modelValue instanceof Array) {
        return this.modelValue.includes(this.value);
      }

      return this.modelValue === this.trueValue;
    },
    computedClass() {
      return `pa-${this.type} pa-${this.size}${
        this.loading ? ' pa-loading' : ''
      }`;
    },
  },
  methods: {
    /**
     * Triggers when there is interaction with the switch
     * Checks if the v-model is of type array or string in this case
     * If is is of type array, and the checked state of the switch is true,
     * the value prop of the checkbox ix pushed into the model to update the options,
     * if it is false, the value is spliced out of the model
     * If the v-model is of type string or any other type, the model is updated and
     * the value of the checked is updated accordingly
     * */
    updateInput(event) {
      const isChecked = event.target.checked;

      if (this.modelValue instanceof Array) {
        const newValue = [...this.modelValue];

        if (isChecked) newValue.push(this.value);
        else newValue.splice(newValue.indexOf(this.value), 1);

        /**
         * @event change
         * @param {boolean || string} newValue bound to the switch
         * */
        this.$emit('change', newValue);
      } else {
        /**
         * @event change
         * @param {boolean} isChecked new value of prop value after change
         * updateInput has been called
         * */
        this.$emit('change', isChecked ? this.trueValue : this.falseValue);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import './switch';
</style>
